import { useCallback, useEffect, useState } from 'react'
import {
  CampaignDetailModelState,
  FormCondition,
  FormConditionCondition,
} from '@urbanx/agx.campaigns.client'
import { AgxButton } from '@urbanx/agx.ui-components2'
import { useSelectedCampaign } from 'hooks'
import { NewFormConditionModal } from './NewFormConditionModal'

interface AddSubjectToFinanceProps {
  id: string
  onConfirm: (subjectToFinance: FormCondition) => void
}

export function AddSubjectToFinance({
  id,
  onConfirm,
}: AddSubjectToFinanceProps) {
  const { campaign } = useSelectedCampaign()
  const [validate, setValidate] = useState(false)
  const [showAddSubjectToFinanceModal, setShowAddSubjectToFinanceModal] =
    useState(false)
  const [subjectToFinance, setSubjectToFinance] = useState<
    FormCondition | undefined
  >(undefined)
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined
  )

  useEffect(() => {
    setValidate(false)
  }, [subjectToFinance])

  useEffect(() => {
    setErrorMessage(undefined)
    setSubjectToFinance(undefined)
  }, [showAddSubjectToFinanceModal])

  const handleConfirmClick = useCallback(() => {
    setValidate(true)
    if (!subjectToFinance || !subjectToFinance.condition) {
      setErrorMessage('Select a condition')
      return
    } else if (
      subjectToFinance.condition === FormConditionCondition.OnSpecificDate &&
      !subjectToFinance.referenceDate
    ) {
      setErrorMessage('Select a date')
      return
    } else if (
      subjectToFinance.condition === FormConditionCondition.NdaysFromDate &&
      !subjectToFinance.daysFromDate
    ) {
      setErrorMessage('Enter number of days')
      return
    } else if (
      subjectToFinance.condition ===
        FormConditionCondition.UponSatisfactionOfSpecialCondition &&
      !subjectToFinance.specialConditionId
    ) {
      setErrorMessage('Enter special condition clause')
      return
    }

    onConfirm(subjectToFinance)
    setShowAddSubjectToFinanceModal(false)
  }, [subjectToFinance])

  // NSW does not allow 'Satisfaction of Special Condition' option
  const isNSW = campaign?.state === CampaignDetailModelState.NSW

  const onDateConditions = isNSW
    ? [FormConditionCondition.OnSpecificDate]
    : [
        FormConditionCondition.OnSpecificDate,
        FormConditionCondition.UponSatisfactionOfSpecialCondition,
      ]

  const fromDateConditions = isNSW
    ? [FormConditionCondition.NdaysFromDate]
    : [
        FormConditionCondition.NdaysFromDate,
        FormConditionCondition.UponSatisfactionOfSpecialCondition,
      ]

  return (
    <>
      <AgxButton
        id="add-finance"
        variant="hollow"
        text="Add Finance"
        onClick={() => setShowAddSubjectToFinanceModal(true)}
      />
      {showAddSubjectToFinanceModal && (
        <NewFormConditionModal
          id={id}
          modalTitle="Add Finance"
          formCondition={subjectToFinance}
          onDateConditions={onDateConditions}
          fromDateConditions={fromDateConditions}
          errorMessage={errorMessage}
          setFormCondition={setSubjectToFinance}
          onConfirmClick={handleConfirmClick}
          openModal={setShowAddSubjectToFinanceModal}
          validate={validate}
        />
      )}
    </>
  )
}
